import isEmpty from 'lodash/isEmpty';
import { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  serializationOptionsForNumberValue,
  useSearchParamState,
} from 'utils/hooks/useSearchParamState';
import { useExperimentTakeover } from 'modules/experiments/takeover';
import { NEW_WORKFLOW_ID } from '../constants';
import { WorkflowType } from '../enums';
import {
  WorkflowsRootState,
  getIsInProgressTotalPublishedView,
  getNodeById,
  getWorkflowView,
  openNodeRequest,
  updateWorkflowView,
} from '../ducks';
import { WorkflowView } from '../constants/workflow';

type InitialWorkflowLoaded = {
  id: string | number;
  workflowType: WorkflowType;
};

type WorkflowChartConfig = {
  workflowView?: WorkflowView;
};

type FeatureFlags = Record<string, boolean>;

interface WorkflowContextState {
  initialWorkflowLoaded?: InitialWorkflowLoaded;
  featureFlags: FeatureFlags;
  nodeId: number;
  experimentId: number;
  showExitRuleSlideOver: boolean;
  setShowExitSlideover: (value: boolean) => void;
  workflowChartConfig: WorkflowChartConfig;
  setWorkflowView: (view: WorkflowView) => void;
}

const WorkflowContext = createContext<WorkflowContextState>({
  initialWorkflowLoaded: undefined,
  featureFlags: {},
  workflowChartConfig: { workflowView: WorkflowView.Total },
  nodeId: 0,
  experimentId: 0,
  showExitRuleSlideOver: false,
  setShowExitSlideover: () => {},
  setWorkflowView: () => {},
});

type WorkflowContextProps = {
  children: React.ReactNode | React.ReactNode[];
  match: { params: { workflowId?: string } };
  location: { search: string };
};

const hydrateInitialWorkflowLoaded = (workflowId: string | number, workflowType: WorkflowType) => ({
  id: workflowId,
  workflowType,
});

const getInitialWorkflowLoaded = (workflowId?: string, search?: string) => {
  const type = new URLSearchParams(search)
    ?.get('workflowType')
    ?.toLocaleUpperCase() as keyof typeof WorkflowType; // Try to set the parsed query string key to be the key of the Enum.

  const workflowType = WorkflowType[type in WorkflowType ? type : 'PUBLISHED']; // Check if the that key exists in the enum, if not default to Published.
  return hydrateInitialWorkflowLoaded(workflowId || NEW_WORKFLOW_ID, workflowType);
};

export const WorkflowContextProvider = ({ children, match, location }: WorkflowContextProps) => {
  const isInProgressTotalPublishedViewEnabled = useSelector(getIsInProgressTotalPublishedView);

  useEffect(() => {
    dispatch(updateWorkflowView(WorkflowView.InProgress));
  }, []);

  const [showExitRuleSlideOver, setShowExitSlideover] = useState(false);

  const dispatch = useDispatch();
  const { openExperimentTakeover } = useExperimentTakeover();

  const [nodeId] = useSearchParamState({
    searchParamKey: 'nodeId',
    stateValueWhenSearchParamIsUnset: 0,
    ...serializationOptionsForNumberValue,
  });

  const [experimentId] = useSearchParamState({
    searchParamKey: 'experimentId',
    stateValueWhenSearchParamIsUnset: 0,
    ...serializationOptionsForNumberValue,
  });

  const node = useSelector((state: WorkflowsRootState) =>
    getNodeById(state, nodeId as unknown as string),
  );

  const workflowView = useSelector(getWorkflowView);

  const nodeIdExists = !isEmpty(node);

  /* This is the original workflow that we load when the studio initially loads, we won't be able to change it and should only reference this value when the app it boostrapped * */
  const [initialWorkflowLoaded] = useState<InitialWorkflowLoaded>(
    getInitialWorkflowLoaded(match?.params?.workflowId, location?.search),
  );

  useEffect(() => {
    if (nodeId && nodeIdExists) {
      dispatch(openNodeRequest(nodeId.toString(), {}));
    }

    if (experimentId) {
      openExperimentTakeover(experimentId);
    }
  }, [nodeId, nodeIdExists, experimentId]);

  const setWorkflowView = (view: WorkflowView) => {
    dispatch(updateWorkflowView(view));
  };

  const context: WorkflowContextState = {
    initialWorkflowLoaded,
    featureFlags: {},
    nodeId,
    experimentId,
    showExitRuleSlideOver,
    setShowExitSlideover,
    workflowChartConfig: { workflowView },
    setWorkflowView,
  };

  return <WorkflowContext.Provider value={context}>{children}</WorkflowContext.Provider>;
};

export const useWorkflowContext = () => useContext(WorkflowContext);
